// Number formatting (de-DE) and parsing. Pure, no DOM.

export const NNBSP = ' '; // narrow no-break space between value and unit

const cache = new Map();
function nf(min, max) {
  const key = min + ':' + max;
  if (!cache.has(key)) {
    cache.set(key, new Intl.NumberFormat('de-DE', { minimumFractionDigits: min, maximumFractionDigits: max }));
  }
  return cache.get(key);
}

/** Format a number in de-DE. `decimals` fixes the digits, or pass {min, max}. */
export function fmt(value, decimals = 0) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  const { min, max } = typeof decimals === 'number' ? { min: decimals, max: decimals } : decimals;
  const out = nf(min ?? 0, max ?? min ?? 0).format(value);
  return out === '-0' || /^-0(,0+)?$/.test(out) ? out.slice(1) : out;
}

/** Value with unit, separated by a narrow no-break space. */
export function fmtUnit(value, unit, decimals = 0) {
  return fmt(value, decimals) + NNBSP + unit;
}

export function fmtEuro(value, decimals = 2) {
  return fmtUnit(value, '€', decimals);
}

/** Pick sensible decimals for a magnitude (for display only). */
export function autoDecimals(value) {
  const a = Math.abs(value);
  if (a === 0) return 0;
  if (a < 1) return 2;
  if (a < 10) return 1;
  return 0;
}

/**
 * Parse user input that may use comma or dot as decimal separator.
 * Returns NaN for empty or invalid input. Thousands separators are not accepted
 * except "1.234,5" (dot before comma), which is common in de-DE.
 */
export function parseDecimal(input) {
  if (typeof input === 'number') return input;
  if (input === null || input === undefined) return NaN;
  let s = String(input).trim().replace(/\s| | /g, '');
  if (s === '') return NaN;
  if (s.includes(',') && s.includes('.')) {
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) s = s.replace(/\./g, '').replace(',', '.');
    else return NaN;
  } else {
    s = s.replace(',', '.');
  }
  if (!/^[+-]?(\d+\.?\d*|\.\d+)$/.test(s)) return NaN;
  return Number(s);
}

export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/** Round only for display or equality checks, never inside calculations. */
export function round(value, decimals = 0) {
  const f = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * f) / f;
}
